import React from "react";
import styles from "../components/chat/MessageRenderer.module.css";
import { tokenizeLinks } from "./linkTokens";

// The only elements a LivePerson rep can produce with the composer toolbar. The backend
// (rep_text.py) already sanitizes to this set; this is a second, explicit allowlist so the
// renderer can never create anything else — no dangerouslySetInnerHTML, no script exec.
const ALLOWED = new Set(["strong", "em", "u", "ul", "ol", "li", "p"]);

/** Turn a plain text run into text + anchors. LivePerson delivers rep hyperlinks as
 *  markdown `[text](url)` (we declared MARKDOWN_HYPERLINKS) and bare URLs even inside an
 *  otherwise-HTML message, so text nodes here get the same linkifying as the plain path. */
function renderTextWithLinks(text: string): React.ReactNode {
  return tokenizeLinks(text).map((t, i) =>
    t.kind === "link" ? (
      <a key={i} className={styles.link} href={t.href} target="_blank" rel="noopener noreferrer">
        {t.text}
      </a>
    ) : (
      <React.Fragment key={i}>{t.text}</React.Fragment>
    ),
  );
}

function nodeToReact(node: ChildNode, key: number, inLink: boolean): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || "";
    // Don't re-linkify inside a real <a> (would nest links); everywhere else, linkify.
    return inLink ? text : <React.Fragment key={key}>{renderTextWithLinks(text)}</React.Fragment>;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null; // drop comments etc.
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "br") return <br key={key} />;
  if (tag === "a") {
    const href = el.getAttribute("href") || "";
    if (/^https?:\/\//i.test(href)) {
      const children = Array.from(el.childNodes).map((c, i) => nodeToReact(c, i, true));
      return (
        <a key={key} className={styles.link} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    // non-http link → not a real link; unwrap and still linkify its text
    const children = Array.from(el.childNodes).map((c, i) => nodeToReact(c, i, inLink));
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }
  const children = Array.from(el.childNodes).map((c, i) => nodeToReact(c, i, inLink));
  if (ALLOWED.has(tag)) {
    return React.createElement(tag, { key }, children.length ? children : undefined);
  }
  return <React.Fragment key={key}>{children}</React.Fragment>; // unknown tag → unwrap to its text
}

/**
 * Render a LivePerson rep's sanitized-HTML message (bold/italic/underline/lists/links)
 * as React elements through a strict allowlist. Underline is why this exists — markdown
 * could not express it. Text runs are linkified via tokenizeLinks so markdown links and
 * bare URLs the rep pasted render as anchors too. Input is already sanitized server-side;
 * DOMParser here never executes scripts and we only emit allowlisted elements.
 */
export function renderRepHtml(htmlStr: string): React.ReactNode {
  if (typeof DOMParser === "undefined") return htmlStr; // non-DOM env guard
  const doc = new DOMParser().parseFromString(htmlStr, "text/html");
  return Array.from(doc.body.childNodes).map((n, i) => nodeToReact(n, i, false));
}
